/**
 * One team's injury list, or its empty state, reused by the game detail
 * Injuries tab and the team profile Injuries tab. ISSUE-984.
 *
 * The picture is the shared player headshot (ISSUE-983): the stored photo
 * the server sends, or the silhouette when there is none. The row prints the
 * name beside it.
 *
 * A row with a playerId opens the player page when the caller names the team (ISSUE-1168); a row without one stays text.
 */
import { FOCUS_RING } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { PlayerHeadshot } from "../playerProfile/PlayerHeadshot";
import {
  INJURIES_NO_REPORT_LABEL,
  INJURIES_NONE_LABEL,
  injuryRowPlayerSearch,
  toInjuryRowModels,
  type typeInjuryListEntry,
  type typeInjuryListTeam,
} from "./injuryListLogic";

export const InjuryList = (props: {
  hasReport: boolean;
  entries: readonly typeInjuryListEntry[];
  team?: typeInjuryListTeam;
}): ReactNode => {
  if (!props.hasReport) {
    return (
      <p data-testid="injuries-empty" className="text-body text-ink-secondary">
        {INJURIES_NO_REPORT_LABEL}
      </p>
    );
  }

  const rows = toInjuryRowModels(props.entries);
  if (rows.length === 0) {
    return (
      <p data-testid="injuries-empty" className="text-body text-ink-secondary">
        {INJURIES_NONE_LABEL}
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-3" data-testid="injury-list">
      {rows.map((row) => {
        const search =
          props.team === undefined
            ? null
            : injuryRowPlayerSearch(row, props.team);
        const content = (
          <>
            <PlayerHeadshot headshotUrl={row.headshotUrl} size="lg" />
            <div className="flex min-w-0 flex-col">
              <span
                data-testid="injury-row-name"
                className="text-body-strong text-ink"
              >
                {row.name}
              </span>
              <span
                data-testid="injury-row-status"
                className="text-caption text-ink-secondary"
              >
                {row.statusLine}
              </span>
            </div>
            {row.position !== null ? (
              <span
                data-testid="injury-row-position"
                className="ml-auto text-caption text-ink-tertiary"
              >
                {row.position}
              </span>
            ) : null}
          </>
        );
        if (search === null) {
          return (
            <li
              key={row.key}
              data-testid="injury-row"
              className="flex items-center gap-3"
            >
              {content}
            </li>
          );
        }
        return (
          <li key={row.key} data-testid="injury-row">
            <Link
              to="/scores/player"
              search={search}
              data-testid="injury-row-link"
              className={`flex min-h-touch-target items-center gap-3 text-ink no-underline ${FOCUS_RING}`}
            >
              {content}
            </Link>
          </li>
        );
      })}
    </ul>
  );
};
